/**
 * Minimal i18n primitive for the design system.
 *
 * Deliberately not a full i18n framework (no pluralization rules, no ICU
 * message syntax, no lazy-loaded bundles) — the project has exactly two
 * locales and no build step that extracts strings, so a plain lookup table
 * plus a React context is all that's needed. Matches the project's existing
 * "no infrastructure before concrete need" approach (see settings.ts).
 */

export type Locale = 'de' | 'en';

export const DEFAULT_LOCALE: Locale = 'de';

/** A single piece of copy in every supported locale. */
export type LocalizedText = Record<Locale, string>;

/** A flat string dictionary, one entry per locale, keyed by translation key. */
export type Dictionary<Key extends string = string> = Record<Key, LocalizedText>;

/** Reads one piece of localized text for the given locale. */
export function localize(text: LocalizedText, locale: Locale): string {
  return text[locale];
}

/**
 * Builds a `t(key)` lookup bound to one dictionary and locale. Use for short,
 * key-addressed UI strings (buttons, labels, nav items) — for prose-heavy
 * content (tutorial chapters, FAQ answers), read `LocalizedText` entries
 * directly via `localize()` instead, since a flat key namespace doesn't fit
 * long-form copy well.
 */
export function createTranslator<Key extends string>(dictionary: Dictionary<Key>, locale: Locale) {
  return (key: Key): string => dictionary[key][locale];
}
